import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as GenerateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as RunIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  Expected,
  MatrixCell,
  Material,
  RunDetail,
  Schedule,
  TargetCase,
  TargetInfo,
  TargetType,
  TestCase,
  TestRun,
  TestsApi,
  fmtCredits,
  fmtPctValue,
  runIsActive,
  useTestsAccess,
} from '../../services/testsApi';
import { SectionTitle, StatCard } from '../performance/common';
import { ExpectedEditor, InputChips, Score, expectedSummary, modelLabel, timeAgo } from './common';
import { CaseEditorDialog, MaterialPickerDialog, errorText } from './dialogs';
import { ApplyPicksDialog } from './ApplyPicksDialog';
import { ScoreTile, cellStatus } from './CaseMatrix';
import { GenerateCasesDialog } from './GenerateCasesDialog';
import { ImproveWithLearning } from './ImproveWithLearning';
import { ModelComparisonTable, rowsFromSummary } from './ModelComparison';
import { RunPanelDialog } from './RunPanel';

export const runUrl = (runId: string, customerId?: string | null) =>
  `/dashboard/tests/runs/${runId}${customerId ? `?customer_id=${encodeURIComponent(customerId)}` : ''}`;

const STATUS_COLORS: Record<TestRun['status'], 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  queued: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};

function useRunDetail(api: TestsApi, runId: string | null | undefined): RunDetail | null {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  useEffect(() => {
    setDetail(null);
    if (!runId) return;
    let cancelled = false;
    api
      .run(runId)
      .then(d => !cancelled && setDetail(d))
      .catch(() => !cancelled && setDetail(null));
    return () => {
      cancelled = true;
    };
  }, [api, runId]);
  return detail;
}

export const RunStatus: React.FC<{ run: TestRun }> = ({ run }) => {
  const p = run.progress || {};
  if (runIsActive(run) && p.total) {
    const pct = Math.round((100 * (p.done || 0)) / p.total);
    return (
      <Box sx={{ minWidth: 120 }}>
        <Typography variant="caption">
          {run.status === 'queued' ? 'Queued' : `${p.done || 0} of ${p.total}`}
        </Typography>
        <LinearProgress variant="determinate" value={pct} />
      </Box>
    );
  }
  return (
    <Tooltip title={run.error || (p.capped ? 'Stopped at the credit cap' : '')}>
      <Chip size="small" color={STATUS_COLORS[run.status]} label={p.capped ? 'capped' : run.status} />
    </Tooltip>
  );
};

/** The Tests tab on a function's or chain's page. `targetId` is a function version id or a chain id. */
export const TargetTests: React.FC<{ targetType: TargetType; targetId: string; customerId?: string | null }> = ({
  targetType,
  targetId,
  customerId,
}) => {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const { api, access, enabled, loading } = useTestsAccess(getIdToken, customerId);
  const [data, setData] = useState<{ target: TargetInfo; cases: TargetCase[]; runs: TestRun[]; schedule: Schedule | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [runOpen, setRunOpen] = useState(false);
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<TestCase | null>(null);
  const [expectedFor, setExpectedFor] = useState<TargetCase | null>(null);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [pickCases, setPickCases] = useState(false);
  const [pickMaterials, setPickMaterials] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = useCallback(() => setReload(r => r + 1), []);

  // `?add=library` (from a platform route's "Set up tests") opens the library picker once.
  useEffect(() => {
    if (searchParams.get('add') !== 'library' || !enabled) return;
    setPickCases(true);
    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .target(targetType, targetId)
      .then(d => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch(e => !cancelled && setError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [api, enabled, targetType, targetId, reload]);

  const active = !!data?.runs.some(runIsActive);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [active, refresh]);

  const cases = useMemo(() => data?.cases || [], [data]);
  const enabledCases = cases.filter(c => c.enabled);
  const needsVision = enabledCases.some(c => c.kinds.some(k => k === 'image' || k === 'video'));
  const lastDone = data?.runs.find(r => r.status === 'completed');
  const compareRun = data?.runs.find(r => r.status === 'completed' && r.models_tested > 1) || lastDone;
  const compareDetail = useRunDetail(api, compareRun?.id);
  const latestDetail = useRunDetail(api, lastDone && lastDone.id !== compareRun?.id ? lastDone.id : null);
  const caseDetail = latestDetail || (lastDone?.id === compareRun?.id ? compareDetail : null);

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;
  if (!enabled) return <Alert severity="info">Tests aren't turned on for this account yet.</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;

  const { target } = data;
  const judge = access?.judge_function;
  const isFunction = targetType === 'function';
  const canGenerate = !target.is_judge;
  const compareSummary = compareDetail?.run.summary || null;
  const canApply = isFunction && !!compareRun && compareRun.models_tested > 1 && !!compareSummary?.picks?.recommended;

  const undoApply = async () => {
    if (!window.confirm(`Put ${target.name}'s models back to ${(target.applied?.previous_models || []).map(m => modelLabel(m)).join(', ')}?`)) return;
    try {
      await api.undoTargetPicks(target.target_id);
      setNotice('Models put back as they were.');
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const caseModel = caseDetail ? caseDetail.run.summary?.default_model || caseDetail.run.default_model : null;
  const failingNow =
    caseDetail && caseModel
      ? caseDetail.cases.filter(c => ['fail', 'partial', 'error'].includes(cellStatus(caseDetail.matrix[c.id]?.[caseModel]))).length
      : 0;
  const ownAccount = !!access && (!customerId || customerId === access.account_id);
  const toggleLink = async (c: TargetCase, patch: { enabled?: boolean; critical?: boolean }) => {
    try {
      await api.updateLink(targetType, target.target_id, c.link_id, patch);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const removeLink = async (c: TargetCase) => {
    if (!window.confirm(`Remove "${c.name}" from ${target.name}? The case stays in the Test Library.`)) return;
    try {
      await api.deleteLink(targetType, target.target_id, c.link_id);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const addCases = async (ids: string[]) => {
    setPickCases(false);
    if (!ids.length) return;
    try {
      const r = await api.linkCases(ids, targetType, target.target_id);
      setNotice(`Added ${r.linked + r.re_enabled} case${r.linked + r.re_enabled === 1 ? '' : 's'}. Add what each should return.`);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const addMaterials = async (materials: Material[]) => {
    setPickMaterials(false);
    try {
      const ids: string[] = [];
      for (const m of materials) {
        const c = await api.createCase({ name: m.name, tags: m.tags, inputs: [{ field: 'input', material_id: m.id }] });
        ids.push(c.id);
      }
      await addCases(ids);
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <Box>
      {target.is_judge && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This function grades test outputs for your account. Edit its prompt or models like any function; learning uses your verdict
          overrides as feedback.
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <StatCard label="Test cases" value={`${enabledCases.length}`} caption={`${enabledCases.filter(c => c.has_expectation).length} with an expected output`} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard
            label={`Accuracy${lastDone?.default_model ? ` (${modelLabel(lastDone.default_model)})` : ''}`}
            value={<Score value={lastDone?.default_accuracy} />}
            caption={lastDone ? `${fmtPctValue(lastDone.default_pass_rate)} of cases passing · ${timeAgo(lastDone.completed_at)}` : 'Not tested yet'}
            tooltip="0–100 average score on the latest completed run, for the current default model."
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard
            label="Recommended model"
            value={<Typography variant="h6">{compareRun && compareRun.models_tested > 1 && compareRun.best_model ? modelLabel(compareRun.best_model) : '–'}</Typography>}
            caption={compareRun && compareRun.models_tested > 1 ? `From ${compareRun.models_tested} models compared` : 'Compare models to get one'}
          />
        </Box>
      </Stack>

      {compareRun && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle
            title="Model comparison"
            help="Every model from the latest run that compared models. Green marks the best accuracy, speed and cost. Open the run for every output, the judge's reasoning and a per-case grid."
            action={
              <Stack direction="row" spacing={1}>
                {canApply && (
                  <Tooltip title="Make the Recommended model the default and another pick the first backup, with a preview and undo">
                    <Button variant="contained" onClick={() => setApplyOpen(true)}>
                      Use these models
                    </Button>
                  </Tooltip>
                )}
                <Button variant="outlined" component={RouterLink} to={runUrl(compareRun.id, customerId)}>
                  Open full results
                </Button>
              </Stack>
            }
          />
          {target.applied && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Models set from a test {timeAgo(target.applied.at)}
              {target.applied.by ? ` by ${target.applied.by}` : ''}: default {modelLabel(target.applied.models[0])}
              {target.applied.models[1] ? `, first backup ${modelLabel(target.applied.models[1])}` : ''}.{' '}
              <Link component="button" type="button" variant="body2" onClick={undoApply} sx={{ verticalAlign: 'baseline' }}>
                Undo
              </Link>
            </Alert>
          )}
          {!compareSummary ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                {compareRun.completed_at ? new Date(compareRun.completed_at).toLocaleString() : ''} · {compareRun.config.case_ids?.length ?? enabledCases.length} cases ×{' '}
                {compareRun.config.repeats || 1} run{(compareRun.config.repeats || 1) === 1 ? '' : 's'} each · {fmtCredits(compareRun.credits_used)} credits
                {compareRun.models_tested < 2 ? ' · run tests on more models to compare them' : ''}
              </Typography>
              {compareSummary.recommendation && compareRun.models_tested > 1 && (
                <Alert severity={compareSummary.recommendation.action === 'switch' ? 'info' : 'success'} sx={{ mb: 1 }}>
                  {compareSummary.recommendation.text}
                  {compareSummary.recommendation.note ? ` ${compareSummary.recommendation.note}` : ''}
                </Alert>
              )}
              {compareSummary.picks?.note && compareRun.models_tested > 1 && (
                <Typography variant="caption" color="warning.main" component="div" sx={{ mb: 1 }}>
                  {compareSummary.picks.note}
                </Typography>
              )}
              <ModelComparisonTable
                rows={rowsFromSummary(compareSummary, (compareSummary.rankings?.balanced || []).map(r => r.model))}
                defaultModel={target.default_model ?? compareSummary.default_model}
                picks={compareRun.models_tested > 1 ? compareSummary.picks : null}
                deadline={compareSummary.deadline_s}
                bar={compareSummary.picks?.bar?.accuracy}
                onRowClick={m => navigate(`${runUrl(compareRun.id, customerId)}${customerId ? '&' : '?'}model=${encodeURIComponent(m)}`)}
              />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Click a model to see its outputs.
              </Typography>
            </>
          )}
        </Box>
      )}

      <SectionTitle
        title="Test cases"
        help={`What ${target.name} should return for each input. Plain descriptions are fine; the judge reads for meaning.`}
        action={
          <Stack direction="row" spacing={1}>
            {ownAccount && !target.is_judge && (
              <ImproveWithLearning
                getToken={getIdToken}
                targetType={targetType}
                targetId={target.target_id}
                targetName={target.name}
                failing={failingNow}
              />
            )}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={e => setAddAnchor(e.currentTarget)}>
              Add
            </Button>
            <Button variant="contained" startIcon={<RunIcon />} onClick={() => setRunOpen(true)} disabled={!enabledCases.length}>
              Run tests
            </Button>
          </Stack>
        }
      />
      <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
        {canGenerate && (
          <MenuItem onClick={() => { setAddAnchor(null); setGenerateOpen(true); }}>
            <ListItemIcon><GenerateIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary="Generate with AI…"
              secondary={`Drafts cases from ${target.name}'s definition, then you pick which to keep`}
            />
          </MenuItem>
        )}
        {canGenerate && <Divider />}
        <MenuItem onClick={() => { setAddAnchor(null); setNewCaseOpen(true); }}>New test case</MenuItem>
        <MenuItem onClick={() => { setAddAnchor(null); setPickCases(true); }}>Existing test cases from the library</MenuItem>
        <MenuItem onClick={() => { setAddAnchor(null); setPickMaterials(true); }}>Files from the library (one case each)</MenuItem>
      </Menu>
      {!cases.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography gutterBottom>No test cases yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            Add some from the Test Library, create one here{isFunction ? ', or use "Save as test case" after trying the function' : ''}.
          </Typography>
          {canGenerate && (
            <Button variant="contained" startIcon={<GenerateIcon />} onClick={() => setGenerateOpen(true)} sx={{ mt: 2 }}>
              Generate test cases
            </Button>
          )}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">On</TableCell>
                <TableCell>Case</TableCell>
                <TableCell>Input</TableCell>
                <TableCell>Expected output</TableCell>
                <TableCell align="right">
                  <Tooltip title={caseModel ? `Score on ${modelLabel(caseModel)} in the latest completed run` : 'Run tests to see scores'}>
                    <span>Latest score</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="A critical case that fails makes the model ineligible for a recommendation.">
                    <span>Critical</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {cases.map(c => (
                <TableRow key={c.link_id} hover sx={{ opacity: c.enabled ? 1 : 0.55 }}>
                  <TableCell padding="checkbox">
                    <Switch size="small" checked={c.enabled} onChange={e => toggleLink(c, { enabled: e.target.checked })} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <InputChips preview={c.preview} max={2} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    {c.has_expectation ? (
                      <Link component="button" type="button" variant="body2" onClick={() => setExpectedFor(c)} sx={{ textAlign: 'left' }}>
                        {expectedSummary(c.expected).slice(0, 160) || 'Edit'}
                      </Link>
                    ) : (
                      <Button size="small" onClick={() => setExpectedFor(c)}>
                        Add expected output
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <CaseScore
                      cell={caseModel ? caseDetail?.matrix[c.case_id]?.[caseModel] : undefined}
                      onClick={
                        caseDetail && caseModel
                          ? () =>
                              navigate(
                                `${runUrl(caseDetail.run.id, customerId)}${customerId ? '&' : '?'}model=${encodeURIComponent(caseModel)}&case=${c.case_id}`,
                              )
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox size="small" checked={c.critical} onChange={e => toggleLink(c, { critical: e.target.checked })} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit the case's input (shared by everything using it)">
                      <IconButton size="small" onClick={() => setEditingCase(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={`Remove from ${target.name}`}>
                      <IconButton size="small" onClick={() => removeLink(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <SectionTitle
        title="Test runs"
        help="Each run tries the enabled cases on the models you pick. Open a run for every output, the judge's reasoning and the model comparison. Test runs don't count toward stats, time limits, learning or alerts."
        action={
          judge && (
            <Typography variant="caption" color="text.secondary">
              Graded by{' '}
              <Link component={RouterLink} to={`/dashboard/function-runs/${judge.function_id}?tab=performance${customerId ? `&customer_id=${encodeURIComponent(customerId)}` : ''}`}>
                {judge.name}
              </Link>
              {judge.default_model ? ` (${modelLabel(judge.default_model)})` : ''}
            </Typography>
          )
        }
      />
      {!data.runs.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No runs yet.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Models</TableCell>
                <TableCell>Accuracy (default)</TableCell>
                <TableCell>Passing</TableCell>
                <TableCell>Recommended</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.runs.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(runUrl(r.id, customerId))}>
                  <TableCell>
                    <Typography variant="body2">{r.created_at ? new Date(r.created_at).toLocaleString() : '–'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.trigger === 'manual' ? r.created_by || 'manual' : r.trigger}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <RunStatus run={r} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={(r.config.models || []).map(m => modelLabel(m)).join(', ')}>
                      <span>{r.models_tested || (r.config.models || []).length}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Score value={r.default_accuracy} />
                  </TableCell>
                  <TableCell>{fmtPctValue(r.default_pass_rate)}</TableCell>
                  <TableCell>{r.models_tested > 1 ? modelLabel(r.best_model) : '–'}</TableCell>
                  <TableCell align="right">{fmtCredits(r.credits_used)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Link component={RouterLink} to={runUrl(r.id, customerId)} variant="body2" onClick={e => e.stopPropagation()}>
                      View results
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <RunPanelDialog
        open={runOpen}
        api={api}
        target={target}
        caseCount={enabledCases.length}
        needsVision={needsVision}
        onClose={() => setRunOpen(false)}
        onStarted={run => {
          setRunOpen(false);
          navigate(runUrl(run.id, customerId));
        }}
      />
      <CaseEditorDialog
        open={newCaseOpen}
        api={api}
        target={{ type: targetType, id: target.target_id, name: target.name, chain: !isFunction }}
        onClose={() => setNewCaseOpen(false)}
        onSaved={() => {
          setNewCaseOpen(false);
          refresh();
        }}
      />
      <CaseEditorDialog
        open={!!editingCase}
        api={api}
        testCase={editingCase}
        target={{ type: targetType, id: target.target_id, name: target.name, chain: !isFunction }}
        onClose={() => setEditingCase(null)}
        onSaved={() => {
          setEditingCase(null);
          refresh();
        }}
      />
      <ExpectedDialog
        api={api}
        target={target}
        testCase={expectedFor}
        onClose={() => setExpectedFor(null)}
        onSaved={() => {
          setExpectedFor(null);
          refresh();
        }}
      />
      {canApply && compareRun && (
        <ApplyPicksDialog
          open={applyOpen}
          api={api}
          routes={[{ family_id: target.target_id, customer_id: access?.account_id || '', name: target.name }]}
          call={(fallback, dryRun) => api.applyTargetPicks(target.target_id, fallback, dryRun, compareRun.id)}
          fromRun
          onClose={() => setApplyOpen(false)}
          onApplied={applied => {
            setApplyOpen(false);
            if (applied) setNotice(`Saved: ${target.name} now uses the test picks. Undo is above the model comparison.`);
            refresh();
          }}
        />
      )}
      {canGenerate && (
        <GenerateCasesDialog
          open={generateOpen}
          api={api}
          target={target}
          existingCount={cases.length}
          isAdmin={!!access?.is_admin}
          onClose={() => setGenerateOpen(false)}
          onSaved={message => {
            setGenerateOpen(false);
            setNotice(message);
            refresh();
          }}
        />
      )}
      <CasePickerDialog
        open={pickCases}
        api={api}
        exclude={cases.map(c => c.case_id)}
        inputTypes={isFunction ? target.input_types : undefined}
        onClose={() => setPickCases(false)}
        onPick={addCases}
      />
      <MaterialPickerDialog
        open={pickMaterials}
        api={api}
        multiple
        title="Files to test with (each becomes a test case)"
        onClose={() => setPickMaterials(false)}
        onPick={addMaterials}
      />
    </Box>
  );
};

const CaseScore: React.FC<{ cell: MatrixCell | undefined; onClick?: () => void }> = ({ cell, onClick }) =>
  cell ? (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <ScoreTile cell={cell} width={72} onClick={onClick} />
    </Box>
  ) : (
    <Typography variant="caption" color="text.secondary">
      –
    </Typography>
  );

const ExpectedDialog: React.FC<{
  api: TestsApi;
  target: TargetInfo;
  testCase: TargetCase | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ api, target, testCase, onClose, onSaved }) => {
  const [expected, setExpected] = useState<Expected>({});
  const [draftOutput, setDraftOutput] = useState<{ output: unknown; model: string; credits: number } | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (testCase) {
      setExpected(testCase.expected || {});
      setDraftOutput(null);
      setError(null);
      setEditorKey(k => k + 1);
    }
  }, [testCase]);

  const draft = async () => {
    if (!testCase) return;
    setDrafting(true);
    setError(null);
    try {
      const r = await api.suggestExpected(target.target_type, target.target_id, testCase.link_id);
      setExpected(e => ({ ...e, ...r.expected }));
      setDraftOutput({ output: r.output, model: r.model, credits: r.credits });
      setEditorKey(k => k + 1);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (!testCase) return;
    setSaving(true);
    try {
      await api.updateLink(target.target_type, target.target_id, testCase.link_id, { expected });
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!testCase} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Expected output: {testCase?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {testCase && <InputChips preview={testCase.preview} max={4} />}
          <ExpectedEditor key={editorKey} value={expected} onChange={setExpected} />
          {target.target_type === 'function' && (
            <Box>
              <Button size="small" variant="outlined" onClick={draft} disabled={drafting}>
                {drafting ? 'Running the function…' : 'Draft from the current output'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Runs the function once on this input and fills in what it returned. Check it before saving.
              </Typography>
            </Box>
          )}
          {draftOutput && (
            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 220, overflow: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                Output from {modelLabel(draftOutput.model)} · {fmtCredits(draftOutput.credits)} credits
              </Typography>
              <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {typeof draftOutput.output === 'string' ? draftOutput.output : JSON.stringify(draftOutput.output, null, 2)}
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CasePickerDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  exclude: string[];
  /** The function's input types: offers to hide cases it can't take. */
  inputTypes?: string[];
  onClose: () => void;
  onPick: (ids: string[]) => void;
}> = ({ open, api, exclude, inputTypes, onClose, onPick }) => {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<TestCase[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fitsOnly, setFitsOnly] = useState(true);
  const accepts = new Set(inputTypes || []);
  const fits = (c: TestCase) => !inputTypes?.length || c.kinds.every(k => accepts.has(k) || (k === 'json' && accepts.has('text')));

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .cases({ search: search || undefined, page_size: 200 })
        .then(d => !cancelled && setItems(d.items))
        .catch(() => !cancelled && setItems([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, open, search]);

  const shown = (items || []).filter(c => !exclude.includes(c.id) && (!fitsOnly || fits(c)));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add test cases from the library</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ my: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {!!inputTypes?.length && (
          <FormControlLabel
            control={<Checkbox size="small" checked={fitsOnly} onChange={e => setFitsOnly(e.target.checked)} />}
            label={<Typography variant="body2">Only cases this function can take ({inputTypes.join(', ')})</Typography>}
          />
        )}
        {!items ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        ) : !shown.length ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Nothing else to add.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
            {shown.map(c => (
              <FormControlLabel
                key={c.id}
                sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.has(c.id)}
                    onChange={() =>
                      setSelected(s => {
                        const next = new Set(s);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        return next;
                      })
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.name}
                    </Typography>
                    <InputChips preview={c.preview} max={2} />
                  </Box>
                }
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!selected.size} onClick={() => onPick(Array.from(selected))}>
          Add {selected.size || ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
